/**
 * Assessment Blueprint Generator
 *
 * Creates deterministic assessment blueprints with smart question selection.
 *
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║  CRITICAL INVARIANT: ASSESSMENT = MULTIPLE CHOICE ONLY         ║
 * ║                                                                ║
 * ║  This generator ONLY selects multiple_choice questions.        ║
 * ║  This is enforced at THREE levels:                             ║
 * ║  1. Policy engine (defaultPolicy.ts) — MC-only eligible        ║
 * ║  2. Eligibility predicate (assessmentEligibility.ts) — MC guard║
 * ║  3. This generator — isMultipleChoice() filter                 ║
 * ║                                                                ║
 * ║  DO NOT remove the MC filter. It is intentional.               ║
 * ╚══════════════════════════════════════════════════════════════════╝
 *
 * v4.1: Tier-balanced representative distribution replaces mastery-weighted
 * 60/30/10 model. Assessment now measures WHAT a learner can do across the
 * full curriculum, not just their weak points.
 *
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 */

import {
  AssessmentBlueprint,
  AssessmentConfig,
  BlueprintQuestion,
  SelectionPlan,
} from '../types'
import {
  type ModuleId,
  type Question,
  getFamiliesForModule,
  type FamilyDefinition,
  QUESTIONS_BY_FAMILY,
  getAllowedTiersForGroup,
  MODULES,
} from '../../content'
import type { FamilyMasteryState } from '../../mastery'
import { getFamilyMasteryKey } from '../../mastery'
import { GroupId } from '../../config/systemConfig'
import { createRng, SeededRng } from '../../utils/deterministicRng'
import { isAssessmentEligible } from './assessmentEligibility'
import { debug } from '../../utils/logger'
import {
  isBegrijpendLezenFamily,
  isTaalverzorgingTaaFamily,
} from './assessmentDomainConfig'
import { getTierBand, MIN_DOK_BY_TIER_BAND } from '../../governance/tierBands'
// R2-BLUEPRINT-GUARD (F-AS-R1-BLUEPRINT-GUARD-DESYNC):
// The pedagogical guard now runs INSIDE blueprint generation rather than
// AFTER it. This guarantees the blueprint already represents the questions
// the learner will see — eliminating the silent shortfall where the
// runtime guard would fatal-reject items the blueprint had already
// promised. The guard's `evaluateQuestion` is pure, so calling it during
// selection is cheap and deterministic.
import { evaluateQuestion } from '../../practice/logic/pedagogicalGuard'

const DEV = process.env.NODE_ENV === 'development'
function log(...args: unknown[]) {
  if (DEV) debug('[AssessmentBlueprint]', ...args)
}

// ── Blueprint Configuration Constants ────────────────────────────────────

/** Mastery score below which a family is categorized as 'weak' */
export const MASTERY_WEAK_THRESHOLD = 55
/** Mastery score at or above which a family is categorized as 'strong' */
export const MASTERY_STRONG_THRESHOLD = 85

/** @deprecated v4.1: Replaced by tier-balanced distribution. Kept for backward compat. */
export const DISTRIBUTION_WEAK_PCT = 0.6
/** @deprecated v4.1: Replaced by tier-balanced distribution. Kept for backward compat. */
export const DISTRIBUTION_MEDIUM_PCT = 0.3

/** Maximum questions selected per family in a single assessment */
export const MAX_QUESTIONS_PER_FAMILY = 2

// ── Tier Band System (v4.1) ─────────────────────────────────────────────

/** Tier band boundaries for assessment distribution */
const TIER_BAND_LOW_MAX = 6 // Tiers 1-6 (Groep 6 level)
const TIER_BAND_MID_MAX = 12 // Tiers 7-12 (Groep 7 level)
// Tiers 13-16 = high (Groep 8 level)

/** Target distribution across tier bands (when all 3 bands available) */
const TIER_BAND_TARGET_PCT = {
  low: 0.35, // 35% easy — establishes baseline
  mid: 0.4, // 40% medium — core measurement
  high: 0.25, // 25% hard — ceiling detection
}

type TierBandKey = 'low' | 'mid' | 'high'

function getTierBandKey(tier: number): TierBandKey {
  if (tier <= TIER_BAND_LOW_MAX) return 'low'
  if (tier <= TIER_BAND_MID_MAX) return 'mid'
  return 'high'
}

// ── DOK Filter for Assessment (PEDAGOGICAL_CONTRACT_SPEC §6) ────────────
//
// R1 (Sprint 5): tier-band classification AND minimum-DOK table are now
// sourced from governance/tierBands.ts — the single source of truth shared
// with practice/logic/pedagogicalGuard.ts and pipeline/stages/pedagogyStage.ts.
//
// Previously this file maintained its own `getAssessmentTierBand` (identical
// thresholds: ≤4 / ≤8 / ≤12) and its own `MIN_DOK_FOR_ASSESSMENT` table
// (identical values). The duplication was drift-risk: a didactician adjusting
// the floor in tierBands.ts would not have updated this file, causing the
// same question to be accepted by the assessment filter but fatal-rejected
// by the runtime guard (or vice versa). Removing the local copies closes
// that gap.
//
// If assessment ever needs to diverge from practice (e.g. higher baseline),
// add a separate MIN_DOK_FOR_ASSESSMENT_BY_TIER_BAND constant to
// tierBands.ts and document the rationale there — not here.

export const DEFAULT_ASSESSMENT_CONFIG: AssessmentConfig = {
  questionCount: 30,
  moduleFilter: null,
  minModules: 3,
  countsForMastery: false,
  seed: undefined,
}

function categorizeMastery(
  mastery: number,
): 'weak' | 'medium' | 'strong' | 'unseen' {
  if (mastery === 0) return 'unseen'
  if (mastery < MASTERY_WEAK_THRESHOLD) return 'weak'
  if (mastery < MASTERY_STRONG_THRESHOLD) return 'medium'
  return 'strong'
}

function getFamilyMasteryScore(
  familyId: string,
  group: GroupId,
  familyMastery: Record<string, FamilyMasteryState>,
): number {
  const key = getFamilyMasteryKey(familyId, group)
  return familyMastery[key]?.masteryScore || 0
}

function isMultipleChoice(questionType?: string): boolean {
  return !questionType || questionType === 'multiple_choice'
}

function getEligibleFamilies(
  config: AssessmentConfig,
  group: GroupId,
  familyMastery: Record<string, FamilyMasteryState>,
): {
  family: FamilyDefinition
  mastery: number
  category: 'weak' | 'medium' | 'strong' | 'unseen'
  questionCount: number
}[] {
  const allowedTiers = getAllowedTiersForGroup(group)
  const modulesToInclude = config.moduleFilter || MODULES.map((m) => m.id)
  const eligibleFamilies: {
    family: FamilyDefinition
    mastery: number
    category: 'weak' | 'medium' | 'strong' | 'unseen'
    questionCount: number
  }[] = []
  let totalCustomQuestionsSkipped = 0

  modulesToInclude.forEach((moduleId) => {
    const families = getFamiliesForModule(moduleId)
    families.forEach((family) => {
      if (!allowedTiers.has(family.tier)) return

      // ── V2: Family-level TAA filtering ────────────────────────────
      // Both Taalverzorging and Begrijpend Lezen include the 'Taalbegrip' module,
      // but they need different TAA families. When domainId is set, filter accordingly.
      if (config.domainId && moduleId === 'Taalbegrip') {
        if (config.domainId === 'taalverzorging') {
          // Only include TAA-ONT-* and TAA-SOORT-* families
          if (!isTaalverzorgingTaaFamily(family.familyId)) return
        } else if (config.domainId === 'begrijpend-lezen') {
          // Only include TAA-LEZEN-*, TAA-WOORD-*, TAA-SIGNAALWOORD, TAA-ARGUMENTATIE, TAA-TEKSTSOORT
          if (!isBegrijpendLezenFamily(family.familyId)) return
        }
      }

      // ── V2: Targeted mode filtering ────────────────────────────
      if (
        config.mode === 'targeted' &&
        config.targetFamilyIds &&
        config.targetFamilyIds.length > 0
      ) {
        if (!config.targetFamilyIds.includes(family.familyId)) return
      }

      const familyQuestions = QUESTIONS_BY_FAMILY.get(family.familyId) || []
      const questionsInTier = familyQuestions.filter((q) => {
        const inTier = allowedTiers.has(q.tier)
        const isMC = isMultipleChoice(q.questionType)
        if (inTier && !isMC) totalCustomQuestionsSkipped++
        return inTier && isMC
      })
      if (questionsInTier.length === 0) return
      const mastery = getFamilyMasteryScore(
        family.familyId,
        group,
        familyMastery,
      )
      const category = categorizeMastery(mastery)
      eligibleFamilies.push({
        family,
        mastery,
        category,
        questionCount: questionsInTier.length,
      })
    })
  })

  if (totalCustomQuestionsSkipped > 0) {
    log(
      `⚠️ Skipped ${totalCustomQuestionsSkipped} custom question types (assessment requires multiple-choice only)`,
    )
  }
  return eligibleFamilies
}

/**
 * F-AS-R2-TARGETED-TIER-GUARD (May 2026)
 *
 * Pre-flight diagnostic for targeted-mode assessments. Targeted mode lets a
 * teacher hand-pick families for a learner; the previous failure mode was a
 * silent zero-question blueprint when none of the chosen families had any
 * MC questions inside the learner's allowed tier range (e.g. teacher
 * targets a Groep 8-only family for a Groep 6 learner).
 *
 * This helper classifies each requested family ID up front so the caller
 * (useAssessmentSession.startAssessment) can show a precise, actionable
 * error instead of a generic "no questions available" message.
 *
 * Returns `null` for non-targeted configs. Otherwise returns:
 *   - requested:        all IDs the teacher selected
 *   - notFound:         IDs that don't exist in the family registry
 *   - droppedForTier:   IDs whose family.tier is outside the learner's group
 *   - droppedForNoMC:   IDs whose family has questions in tier but zero MC
 *   - eligible:         IDs that survive — at least one MC question in tier
 *
 * `eligible.length === 0` is the signal for "zero-question blueprint".
 */
export interface TargetedModeDiagnosis {
  requested: string[]
  notFound: string[]
  droppedForTier: string[]
  droppedForNoMC: string[]
  eligible: string[]
}

export function diagnoseTargetedMode(
  config: AssessmentConfig,
  selectedGroup: GroupId,
): TargetedModeDiagnosis | null {
  if (
    config.mode !== 'targeted' ||
    !config.targetFamilyIds ||
    config.targetFamilyIds.length === 0
  ) {
    return null
  }

  const allowedTiers = getAllowedTiersForGroup(selectedGroup)
  const requested = [...config.targetFamilyIds]
  const notFound: string[] = []
  const droppedForTier: string[] = []
  const droppedForNoMC: string[] = []
  const eligible: string[] = []

  // Build a familyId → FamilyDefinition map across all modules once.
  const familyById = new Map<string, FamilyDefinition>()
  MODULES.forEach((m) => {
    getFamiliesForModule(m.id).forEach((f) => familyById.set(f.familyId, f))
  })

  for (const familyId of requested) {
    const family = familyById.get(familyId)
    if (!family) {
      notFound.push(familyId)
      continue
    }
    if (!allowedTiers.has(family.tier)) {
      droppedForTier.push(familyId)
      continue
    }
    const familyQuestions = QUESTIONS_BY_FAMILY.get(familyId) || []
    const inTierMC = familyQuestions.some(
      (q) => allowedTiers.has(q.tier) && isMultipleChoice(q.questionType),
    )
    if (!inTierMC) {
      droppedForNoMC.push(familyId)
      continue
    }
    eligible.push(familyId)
  }

  return { requested, notFound, droppedForTier, droppedForNoMC, eligible }
}

/**
 * Stats accumulator for the in-blueprint pedagogical guard pass.
 * R2-BLUEPRINT-GUARD: surfaced in `selectionPlan.guardStats` so admin
 * tooling can see, per blueprint, how many seeds were pre-filtered.
 */
interface BlueprintGuardStats {
  evaluated: number
  passed: number
  fatalRejected: number
  softFailed: number
  reasons: Record<string, number>
}

function makeEmptyGuardStats(): BlueprintGuardStats {
  return {
    evaluated: 0,
    passed: 0,
    fatalRejected: 0,
    softFailed: 0,
    reasons: {},
  }
}

function selectQuestionsFromFamily(
  familyId: string,
  count: number,
  allowedTiers: Set<number>,
  usedQuestionIds: Set<string>,
  rng: SeededRng,
  guardStats: BlueprintGuardStats,
): { id: string; citoDifficulty?: number }[] {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(familyId) || []
  const availableQuestions = familyQuestions.filter((q) => {
    if (!allowedTiers.has(q.tier)) return false
    if (usedQuestionIds.has(q.id)) return false
    if (!isAssessmentEligible(q as Question)) return false

    // DOK minimum filter — exclude low-DOK questions from assessment
    //
    // R3-DOK2 (P1): Missing pedagogy.dok is now treated as DOK 1 (conservative
    // fallback) rather than silently bypassing the filter. Previously a seed
    // without DOK metadata would slip into the mid/high tier bands even though
    // we have no evidence the cognitive load is appropriate. After this fix,
    // such questions are only eligible for the LOW tier band (where minDok=1),
    // which matches their lack of validated higher-order reasoning signal.
    const dok = q.pedagogy?.dok ?? 1
    const band = getTierBand(q.tier)
    const minDok = MIN_DOK_BY_TIER_BAND[band] ?? 1
    if (dok < minDok) {
      const reason =
        q.pedagogy?.dok === undefined ? 'missing → DOK 1' : `${dok}`
      log(
        `   🧠 DOK filter: ${q.id} excluded from assessment (DOK ${reason} < min ${minDok} for band "${band}")`,
      )
      return false
    }

    // R2-BLUEPRINT-GUARD (F-AS-R1-BLUEPRINT-GUARD-DESYNC):
    // Apply the same pedagogical guard the runtime would apply. Fatal
    // rejections (tier-DOK floor, indicator parity) are pre-filtered so
    // the blueprint never promises a question that the runtime would
    // silently drop. Soft fails are allowed through (matching the
    // runtime's behaviour) and just get counted.
    guardStats.evaluated++
    const verdict = evaluateQuestion(q as Question)
    if (verdict.ok) {
      guardStats.passed++
    } else if (verdict.severity === 'soft') {
      guardStats.softFailed++
      guardStats.reasons[verdict.reason ?? 'unknown'] =
        (guardStats.reasons[verdict.reason ?? 'unknown'] ?? 0) + 1
      // soft fails still pass selection
    } else {
      guardStats.fatalRejected++
      guardStats.reasons[verdict.reason ?? 'unknown'] =
        (guardStats.reasons[verdict.reason ?? 'unknown'] ?? 0) + 1
      log(
        `   🛡️ Guard filter: ${q.id} fatally rejected at blueprint time — ${verdict.reason}`,
      )
      return false
    }

    return true
  })
  if (availableQuestions.length === 0) return []

  // v4.4: Sort by citoDifficulty — prefer questions WITH difficulty data,
  // then select a spread across the difficulty range
  const withDifficulty = availableQuestions.filter(
    (q) => q.citoDifficulty !== undefined,
  )
  const withoutDifficulty = availableQuestions.filter(
    (q) => q.citoDifficulty === undefined,
  )

  let selected: typeof availableQuestions

  if (withDifficulty.length >= count) {
    // Enough questions with citoDifficulty — select a spread across the range
    const sorted = [...withDifficulty].sort(
      (a, b) => (a.citoDifficulty ?? 0) - (b.citoDifficulty ?? 0),
    )
    if (count === 1) {
      // Pick from the middle of the range for single selection
      const midIdx = Math.floor(sorted.length / 2)
      selected = [sorted[midIdx]]
    } else {
      // Select evenly spaced across the difficulty range for spread
      selected = []
      for (let i = 0; i < count && i < sorted.length; i++) {
        const idx = Math.round((i / (count - 1)) * (sorted.length - 1))
        if (!selected.includes(sorted[idx])) {
          selected.push(sorted[idx])
        }
      }
      // Fill any remaining with shuffled extras
      if (selected.length < count) {
        const remaining = sorted.filter((q) => !selected.includes(q))
        const shuffledRemaining = rng.shuffle(remaining)
        selected.push(...shuffledRemaining.slice(0, count - selected.length))
      }
    }
  } else {
    // Not enough with citoDifficulty — use all with difficulty, then fill from without
    selected = [...withDifficulty]
    const shuffledWithout = rng.shuffle([...withoutDifficulty])
    selected.push(...shuffledWithout.slice(0, count - selected.length))
  }

  return selected.slice(0, count).map((q) => ({
    id: q.id,
    citoDifficulty: q.citoDifficulty,
  }))
}

export function generateAssessmentBlueprint(
  config: AssessmentConfig,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
): AssessmentBlueprint {
  const seed = config.seed || Date.now()
  const rng = createRng(seed)
  const allowedTiers = getAllowedTiersForGroup(selectedGroup)
  log(`Generating blueprint for group ${selectedGroup}, seed: ${seed}`)
  log(`🔒 Assessment mode: MC only (visuals allowed, no interactive)`)
  log(`📊 Selection strategy: tier-balanced representative distribution (v4.1)`)

  const eligibleFamilies = getEligibleFamilies(
    config,
    selectedGroup,
    familyMastery,
  )
  log(
    `Found ${eligibleFamilies.length} eligible families (assessment-eligible questions only)`,
  )

  // ── v4.1: Tier-balanced distribution ──────────────────────────────
  // Group families by tier band instead of mastery category
  const bandFamilies: Record<TierBandKey, typeof eligibleFamilies> = {
    low: [],
    mid: [],
    high: [],
  }
  eligibleFamilies.forEach((f) => {
    const band = getTierBandKey(f.family.tier)
    bandFamilies[band].push(f)
  })

  // Calculate target questions per band (proportional to available families)
  const availableBands = (['low', 'mid', 'high'] as TierBandKey[]).filter(
    (band) => bandFamilies[band].length > 0,
  )

  let bandTargets: Record<TierBandKey, number>
  if (availableBands.length === 3) {
    // All bands available — use standard distribution
    bandTargets = {
      low: Math.round(config.questionCount * TIER_BAND_TARGET_PCT.low),
      mid: Math.round(config.questionCount * TIER_BAND_TARGET_PCT.mid),
      high:
        config.questionCount -
        Math.round(config.questionCount * TIER_BAND_TARGET_PCT.low) -
        Math.round(config.questionCount * TIER_BAND_TARGET_PCT.mid),
    }
  } else if (availableBands.length === 2) {
    // Two bands — split evenly
    const first = availableBands[0]
    const second = availableBands[1]
    const half = Math.floor(config.questionCount / 2)
    bandTargets = { low: 0, mid: 0, high: 0 }
    bandTargets[first] = half
    bandTargets[second] = config.questionCount - half
  } else {
    // Single band — all questions from it
    bandTargets = { low: 0, mid: 0, high: 0 }
    if (availableBands.length > 0) {
      bandTargets[availableBands[0]] = config.questionCount
    }
  }

  log(
    `Tier bands: low=${bandFamilies.low.length} families, mid=${bandFamilies.mid.length}, high=${bandFamilies.high.length}`,
  )
  log(
    `Targets: low=${bandTargets.low}q, mid=${bandTargets.mid}q, high=${bandTargets.high}q`,
  )

  // ── Select questions per tier band ────────────────────────────────
  const selectedQuestions: BlueprintQuestion[] = []
  const usedQuestionIds = new Set<string>()
  const usedFamilyIds = new Set<string>()
  const actualBandCounts: Record<TierBandKey, number> = {
    low: 0,
    mid: 0,
    high: 0,
  }

  // R2-BLUEPRINT-GUARD: shared guard-stats accumulator. Surfaced on the
  // selectionPlan so admin tooling can audit per-blueprint guard health.
  const guardStats: BlueprintGuardStats = makeEmptyGuardStats()

  // Also track mastery categories for backward-compatible reporting
  let actualWeak = 0
  let actualMedium = 0
  let actualStrong = 0

  function selectFromBand(
    band: TierBandKey,
    families: typeof eligibleFamilies,
    targetCount: number,
  ) {
    const shuffledFamilies = rng.shuffle([...families])
    let selected = 0
    for (const { family, mastery, category } of shuffledFamilies) {
      if (selected >= targetCount) break
      if (usedFamilyIds.has(family.familyId)) continue
      const questionsToSelect = Math.min(
        MAX_QUESTIONS_PER_FAMILY,
        targetCount - selected,
      )
      const questionResults = selectQuestionsFromFamily(
        family.familyId,
        questionsToSelect,
        allowedTiers,
        usedQuestionIds,
        rng,
        guardStats,
      )
      if (questionResults.length === 0) continue
      questionResults.forEach(({ id: qId, citoDifficulty }) => {
        const question = QUESTIONS_BY_FAMILY.get(family.familyId)?.find(
          (q) => q.id === qId,
        )
        if (!question) return
        const masteryCategory = category === 'unseen' ? 'unseen' : category
        selectedQuestions.push({
          questionId: qId,
          familyId: family.familyId,
          tier: question.tier,
          module: question.module,
          masteryCategory,
          citoDifficulty,
        })
        usedQuestionIds.add(qId)
        selected++

        // Track mastery distribution for reporting
        if (masteryCategory === 'weak' || masteryCategory === 'unseen')
          actualWeak++
        else if (masteryCategory === 'medium') actualMedium++
        else actualStrong++
      })
      usedFamilyIds.add(family.familyId)
    }
    // Accumulate (not assign) — F-AS-R2 fallback fill can call this twice for
    // the same band when filling remaining slots. Original `=` semantics were
    // safe only because the first loop calls each band exactly once.
    actualBandCounts[band] = (actualBandCounts[band] ?? 0) + selected
    return selected
  }

  // Select from each band in order
  for (const band of ['low', 'mid', 'high'] as TierBandKey[]) {
    selectFromBand(band, bandFamilies[band], bandTargets[band])
  }

  // Fill remaining from any band with available families.
  // F-AS-R2 fix (May 2026): previously this hardcoded 'mid' as the fallback
  // band, which caused actualBandCounts['mid'] to be incremented with
  // questions drawn from low- and high-band families — misreporting the
  // distribution in SelectionPlan. Now we bucket the unused families by
  // their actual tier-band first, so actualBandCounts reflects reality.
  const remaining = config.questionCount - selectedQuestions.length
  if (remaining > 0) {
    log(`Filling ${remaining} remaining questions from any available band`)
    const unusedFamilies = eligibleFamilies.filter(
      (f) => !usedFamilyIds.has(f.family.familyId),
    )
    if (unusedFamilies.length > 0) {
      let stillRemaining = remaining
      for (const band of ['low', 'mid', 'high'] as TierBandKey[]) {
        if (stillRemaining <= 0) break
        const familiesInBand = unusedFamilies.filter(
          (f) => getTierBandKey(f.family.tier) === band,
        )
        if (familiesInBand.length === 0) continue
        const selected = selectFromBand(band, familiesInBand, stillRemaining)
        stillRemaining -= selected
      }
    }
  }

  // v4.4: Progressive difficulty ordering within tier bands
  // Instead of random shuffle, order questions: low band (easy→hard) → mid (easy→hard) → high (easy→hard)
  // Within each band, questions are sorted by citoDifficulty ascending (questions without citoDifficulty go last)
  const bandOrder: TierBandKey[] = ['low', 'mid', 'high']
  const orderedQuestions: BlueprintQuestion[] = []

  for (const band of bandOrder) {
    const bandQuestions = selectedQuestions.filter(
      (q) => getTierBandKey(q.tier) === band,
    )
    // Sort within band: by citoDifficulty ascending, undefined last
    bandQuestions.sort((a, b) => {
      const aDiff = a.citoDifficulty ?? 999
      const bDiff = b.citoDifficulty ?? 999
      if (aDiff !== bDiff) return aDiff - bDiff
      // Tie-break by tier (lower tier first within same difficulty)
      return a.tier - b.tier
    })
    orderedQuestions.push(...bandQuestions)
  }

  log(
    `📊 Progressive ordering: ${orderedQuestions.filter((q) => q.citoDifficulty !== undefined).length}/${orderedQuestions.length} questions have citoDifficulty`,
  )

  // ── citoDifficulty stats for audit trail ──────────────────────────
  const questionsWithDifficulty = orderedQuestions.filter(
    (q) => q.citoDifficulty !== undefined,
  )
  const questionsWithoutDifficulty = orderedQuestions.filter(
    (q) => q.citoDifficulty === undefined,
  )
  const avgDifficulty =
    questionsWithDifficulty.length > 0
      ? questionsWithDifficulty.reduce(
          (sum, q) => sum + (q.citoDifficulty ?? 0),
          0,
        ) / questionsWithDifficulty.length
      : 0

  // ── Build selection plan with both mastery and tier info ───────────
  const selectionPlan: SelectionPlan = {
    // Legacy mastery-based fields (for backward compat with reports/seeder)
    weakPercentage:
      selectedQuestions.length > 0
        ? Math.round((actualWeak / selectedQuestions.length) * 100)
        : 0,
    mediumPercentage:
      selectedQuestions.length > 0
        ? Math.round((actualMedium / selectedQuestions.length) * 100)
        : 0,
    strongPercentage:
      selectedQuestions.length > 0
        ? Math.round((actualStrong / selectedQuestions.length) * 100)
        : 0,
    actualDistribution: {
      weak: actualWeak,
      medium: actualMedium,
      strong: actualStrong,
    },
    // v4.1: Tier-balanced distribution info
    tierDistribution: {
      targetPct: {
        low: Math.round((bandTargets.low / config.questionCount) * 100),
        mid: Math.round((bandTargets.mid / config.questionCount) * 100),
        high: Math.round((bandTargets.high / config.questionCount) * 100),
      },
      actual: actualBandCounts,
      availableFamilies: {
        low: bandFamilies.low.length,
        mid: bandFamilies.mid.length,
        high: bandFamilies.high.length,
      },
    },
    strategy: 'tier-balanced',
    // v4.4: citoDifficulty coverage
    citoDifficultyStats: {
      withDifficulty: questionsWithDifficulty.length,
      withoutDifficulty: questionsWithoutDifficulty.length,
      averageDifficulty: Math.round(avgDifficulty * 100) / 100,
      progressiveOrdering: true,
    },
    // R2-BLUEPRINT-GUARD: pedagogical-guard pass stats. `fatalRejected`
    // here means "filtered out during selection" — the runtime no longer
    // needs to fatal-reject after the blueprint is built, so the resulting
    // attempt always has exactly `questionCount` items.
    guardStats: {
      evaluated: guardStats.evaluated,
      passed: guardStats.passed,
      fatalRejected: guardStats.fatalRejected,
      softFailed: guardStats.softFailed,
      reasons: guardStats.reasons,
    },
  }

  // Mastery snapshot still useful for audit trail
  const weakFamilies = eligibleFamilies.filter(
    (f) => f.category === 'weak' || f.category === 'unseen',
  )
  const mediumFamilies = eligibleFamilies.filter((f) => f.category === 'medium')
  const strongFamilies = eligibleFamilies.filter((f) => f.category === 'strong')

  const blueprint: AssessmentBlueprint = {
    id: `assessment_${seed}_${selectedGroup}`,
    createdAt: Date.now(),
    selectedGroup,
    config,
    selectionPlan,
    questions: orderedQuestions,
    masterySnapshot: {
      totalFamilies: eligibleFamilies.length,
      weakFamilies: weakFamilies.length,
      mediumFamilies: mediumFamilies.length,
      strongFamilies: strongFamilies.length,
    },
    eligibilityFilter: 'MC_ONLY_WITH_VISUALS',
    pickedQuestionIds: orderedQuestions.map((q) => q.questionId),
  }

  log(
    `✅ Blueprint created: ${blueprint.questions.length} questions — tier-balanced progressive (low: ${actualBandCounts.low}, mid: ${actualBandCounts.mid}, high: ${actualBandCounts.high}), citoDifficulty coverage: ${questionsWithDifficulty.length}/${orderedQuestions.length}`,
  )
  if (guardStats.fatalRejected > 0 || guardStats.softFailed > 0) {
    log(
      `🛡️ Guard pass: ${guardStats.passed}/${guardStats.evaluated} ok · ${guardStats.fatalRejected} fatal · ${guardStats.softFailed} soft`,
    )
  }
  return blueprint
}
